import { useEffect, useState } from "react";
import { Grid, Breadcrumbs } from '@mui/material';
import CustomTable from "../../components/common/CustomTable/CustomTable";
import { ModalConfirm } from "../../components/common/ModalConfirm/ModalConfirm";
import { useProductStore } from "../../hooks/useProductStore";
import { ProductModal } from "./components/ProductModal/ProductModal";
import { emphasize, styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import { ImportedProducts } from "./ImportedProducts";
import { useProductImportStore } from "../../hooks/useProductImportStore";
import { ModalLoading } from "../../components/common/ModalLoading/ModalLoading";
import { CustomSnackbar } from "../../components/common/CustomSnackbar/CustomSnackbar";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800];
    return {
        backgroundColor,
        height: theme.spacing(3),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
    };
});

const columns = [
    { type: 'options', field: 'options', label: 'Acciones', align: 'center' },
    { type: 'text', field: 'code_system', label: 'Código', align: 'center' },
    { type: 'text', field: 'name', label: 'Nombre', align: 'center' },
    { type: 'text', field: 'description', label: 'Descripcion', align: 'center' },
    { type: 'text', field: 'status', label: 'Estado', align: 'center', format: (row) => row.status == 1 ? 'Activado' : 'Desactivado' },
    { type: 'detail', field: 'detail', label: 'Detalle', align: 'center' },
];

export const ProductsView = () => {

    const { setActionProduct, getProducts, getProductsByCompany, deleteProduct, downloadTemplate, importProducts, exportProducts, totalRows } = useProductStore();
    const { ValidateUserImport } = useProductImportStore();

    const [loading, setLoading] = useState<boolean>(false);
    const [loadingValidate, setLoadingValidate] = useState<boolean>(false);
    const [loadingImport, setLoadingImport] = useState<boolean>(false);
    const [products, setProducts] = useState<any>([]);
    const [rowSelected, setRowSelected] = useState<any>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false);
    const [searchTimeout, setSearchTimeout] = useState<any>(null);
    const [importedProducts, setImportedProducts] = useState<boolean>(false);
    const [view, setView] = useState<String>('products');
    const [currentFile, setCurrentFile] = useState<any>(null);
    const [hasImports, setHasImports] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)

    useEffect(() => {
        ValidateUserImportApi();
    }, [])

    useEffect(() => {
        if (search === '') {
            getProductsApi()
        } else {
            const timer = setTimeout(() => getProductsApi(), 500)
            return () => clearTimeout(timer)
        }
    }, [search, page, perPage])

    const getProductsApi = async () => {
        try {
            setLoading(true);
            const response = await getProductsByCompany({ search, page, perPage });
            if (!response) {
                setProducts([]);
                setLoading(false);
                return false;
            } else {
                setProducts(response || []);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
        }
    }

    const ValidateUserImportApi = async () => {
        try {
            setLoadingValidate(true);
            const response = await ValidateUserImport();
            if (response && response === 'El usuario tiene productos para importar.') setHasImports(true);
            setLoadingValidate(false);
        } catch (error) {
            setLoadingValidate(false);
        }
    }

    const onAdd = () => {
        setActionProduct('add');
        setShowModal(true);
    }
    const onEdit = (rowSelected: any) => {
        setActionProduct('edit');
        setRowSelected(rowSelected);
        setShowModal(true);
    }
    const onDelete = (rowSelected: any) => {
        setActionProduct('delete');
        setRowSelected(rowSelected);
        setShowModalConfirm(true);
    }
    const onDetails = (rowSelected: any) => {
        setActionProduct('detail');
        setRowSelected(rowSelected);
        setShowModal(true);
    }
    const onDeleteConfirm = async () => {
        if (rowSelected) {
            setLoading(true);
            const response = await deleteProduct(rowSelected.id)
            if (response) {
                getProductsApi();
                setShowModalConfirm(false);
                setLoading(false);
                setRowSelected('');
            } else {
                setLoading(false);
                return false;
            }
        }
    }

    const onSearch = (term) => {
        setSearch(term)
    }

    const downloadTemplateAPI = async () => {
        try {
            setLoading(true);
            const response = await downloadTemplate();
            if (response) window.open(response, '_blank')
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }

    const onImport = async (event) => {

        setLoadingImport(true);
        const file = event.target.files[0];

        if (file) {
            const fileName = file.name;
            const fileExtension = fileName.split('.').pop();

            if (fileExtension != 'xlsx') return CustomSnackbar('error', 'Selecciona un archivo .xlsx válido');

            setCurrentFile(file);
            const nameAsArray = file.name.split(".") || [];
            const nameWithoutExtension = nameAsArray.filter((word, i) => i != nameAsArray.length - 1);
            const nameFile = nameWithoutExtension.join(".") || "";

            const formData = new FormData();
            formData.append("products_import", file);

            const response: any = await importProducts(formData);
            if (response) {
                setImportedProducts(true); setView('importedProducts'); setLoadingImport(false);
            } else {
                setLoadingImport(false);
                return false;
            }
        }
        setLoadingImport(false);
        return false;
    };

    const redirecInfo = () => {
        setImportedProducts(true)
        setView('importedProducts')
    }

    const clearState = () => {
        setRowSelected('');
    }

    return (
        <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 3 }}>
                <StyledBreadcrumb
                    label={"Maestros"}
                />
                <StyledBreadcrumb
                    label={"productos"}
                    onClick={() => {
                        setView('products');
                        setRowSelected('');
                        setImportedProducts(false);
                        getProductsApi();
                        ValidateUserImportApi();
                    }}
                />
                {
                    importedProducts && (
                        <StyledBreadcrumb
                            label={"importar"}
                            onClick={() => { setView('importedProducts') }}
                        />
                    )
                }
            </Breadcrumbs>
            {
                view == 'products' && (
                    <Grid item xs={12}>
                        <CustomTable
                            title={'Productos'}
                            columns={columns}
                            loading={loading}
                            rows={products || []}
                            onRowClick={() => { }}
                            hasOptions
                            onAddFn={onAdd}
                            onAddFnName={'Nuevo producto'}
                            onDetails={onDetails}
                            onDelete={onDelete}
                            onEdit={onEdit}
                            onSearch={onSearch}
                            onImport={onImport}
                            tooltip_import={'Cargar productos'}
                            template={downloadTemplateAPI}
                            textInfo={hasImports ? 'Ud. tiene productos importados NO guardados.' : false}
                            redirecInfo={redirecInfo}
                            dense
                            totalRows={totalRows}
                        />
                    </Grid>
                )
            }
            {
                view == 'importedProducts' && (
                    <ImportedProducts />
                )
            }
            {
                showModal && (
                    <ProductModal
                        open={showModal}
                        closeModal={() => { setShowModal(false) }}
                        productSelected={rowSelected}
                        listproducts={getProductsApi}
                        clearState={clearState}
                        imported={false}
                    />
                )
            }
            {
                showModalConfirm && (
                    <ModalConfirm
                        open={showModalConfirm}
                        closeModal={() => { setShowModalConfirm(false); clearState() }}
                        onConfirm={onDeleteConfirm}
                        status={loading}
                    />
                )
            }
            {
                loadingImport && (
                    <ModalLoading
                        open={loadingImport}
                        text={'Cargando Productos. Espere un momento...'}
                        status={loadingImport}
                    />
                )
            }
        </>
    )
}